import { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import CustomTable from "../../../../components/custom-table/custom-table";

import {
  isCurrentUserAdmin,
  isCurrentUserSuperAdmin,
} from "@risalabs_frontend_org/auth-wrapper";
import { toast } from "@risalabs_frontend_org/oasis-ui-kit";
import moment from "moment";
import { ToastMessages } from "../../../../components/toasts/toastMessages";
import { useTablesContext } from "../../../../context/tablesContextProvider";
import {
  FilterSection,
  FilterValues,
} from "../../../../data-model/filterValues";
import { FinalWorklistDataResponse } from "../../../../data-model/finalWorklistModel";
import { OrganizationType } from "../../../../enums/organizationTypes";
import { UserGeneralInfoContextKeys } from "../../../../enums/userGeneralInfoContextKeys";
import { fetchAnalyticsDataFromApi } from "../../../../redux/slice/analyticsDataSlice";
import { setFilter } from "../../../../redux/slice/filterValuesSlice";
import {
  appendFinalWorklistOrdersFromApi,
  appendFinalWorklistSearchedOrdersFromApi,
  fetchFinalWorklistOrdersFromApi,
  resetFinalWorklistOrders,
  searchFinalWorklistOrdersFromApi,
} from "../../../../redux/slice/medicalPa/finalWorklistSlice";
import { AppDispatch, RootState } from "../../../../redux/store/store";
import { UserStore } from "../../../../store/UserGeneralInfoStore";
import { authStatusOptionsForFinalWorklist } from "../../../../utils/authStatus";
import { logDataToConsole } from "../../../../utils/customLogger";
import {
  exportDataFromTableForExternalPaOrders,
  exportDataToExcel,
} from "../../../../utils/exportDataToExcel";
import {
  createFilterForAuthStatus,
  createFilterForCreatedAt,
  createFilterForDateOfService,
  createFilterForDrugNames,
  createFilterForLocation,
  createFilterForOrderStatus,
  createFilterForPrimaryInsurance,
  FinalWorklistTableHeaders,
  FinalWorklistTableHeadersForNYCBS,
  generateMedicalPaOrdersTableDataForNonAdmin,
  generatFinalWorklistTableData,
} from "../utils/finalWorklistTableDataGenerator";

const FinalWorklistOrdersTable = ({
  medicalPaOrdersTableRef,
  onDataChange,
  onComplete,
  onDownloadButtonClick,
  onBulkAssignButtonClick,
}: {
  medicalPaOrdersTableRef: React.RefObject<HTMLDivElement>;
  onDataChange: (data: any) => void;
  onComplete: boolean;
  onDownloadButtonClick: (
    id: string,
    rowData?: FinalWorklistDataResponse,
  ) => void;
  onBulkAssignButtonClick: () => void;
}) => {
  const { filters } = useSelector((state: RootState) => state.filterValues);
  const [tableData, setTableData] = useState<any>([]);
  const [searchTerm, setSearchTerm] = useState<string>("");
  const [isTableRefActive, setIsTableRefActive] = useState(false);
  const [filterData, setFilterData] = useState<FilterSection[]>([]);
  const [filterForTable, setFilterForTable] = useState<FilterValues | null>(
    null,
  );
  const [filterState, setFilterState] = useState<boolean>(false);
  const [initialSearch, setInitialSearch] = useState<boolean>(true);
  const [batchNumberRetreived, setBatchNumberRetreived] = useState<number[]>(
    [],
  );
  const [endIndex, setEndIndex] = useState<number>(0);
  const [hasInitialDataLoaded, setHasInitialDataLoaded] =
    useState<boolean>(false);

  const dispatch = useDispatch<AppDispatch>();
  const {
    data: finalWorklistData,
    loading: isLoadingFinalWorklist,
    pageData,
    showInLineLoader,
    error,
  } = useSelector((state: RootState) => state.finalWorklistOrdersData);

  const { data: providerDetails } = useSelector(
    (state: RootState) => state.providerDetails,
  );

  // Get analytics data for filter options
  const { analyticsData, isLoading: isLoadingAnalyticsData } = useSelector(
    (state: RootState) => state.analyticsData,
  );

  const { data: authStatusOptions } = useSelector(
    (state: RootState) => state.authStatusOptions,
  );

  const { getTableDataFromContext, setTableDataForContext } =
    useTablesContext();
  const tableAttributes = getTableDataFromContext("finalWorklistOrders");

  const initialLimit = 100;

  const finalLimitToUse = useMemo(() => {
    const dataFromContext = tableAttributes.lastBatchFetched
      ? tableAttributes.lastBatchFetched
      : 0;
    const limitToFetch =
      dataFromContext === 0
        ? initialLimit
        : (dataFromContext + 1) * initialLimit;
    return limitToFetch;
  }, []);

  const processFilterData = (validFilters: FilterValues[]) => {
    if (!validFilters?.[0]?.["filterCount"]) return {};

    const allFilters = validFilters[0];
    const filters = Object.keys(allFilters).filter((key) => key === "values");
    const filterValues = filters.map((filter) => allFilters[filter]);

    const processedFilters: Record<string, any> = {};

    Object.keys(filterValues?.[0] || {}).forEach((key) => {
      const value = filterValues?.[0]?.[key]?.values;

      // Handle upload_timestamp (CreatedAt) date filter
      if (
        key === "upload_timestamp" &&
        Array.isArray(value) &&
        value.length === 2
      ) {
        const [startDate, endDate] = value;
        processedFilters.upload_date_from = convertDateFormat(startDate);
        processedFilters.upload_date_to = convertDateFormat(endDate);
      }
      // Handle date_of_service date filter (if needed in future)
      else if (
        key === "date_of_service" &&
        Array.isArray(value) &&
        value.length === 2
      ) {
        const [startDate, endDate] = value;
        processedFilters.date_of_service_from = convertDateFormat(startDate);
        processedFilters.date_of_service_to = convertDateFormat(endDate);
      }
      // Handle other filters normally
      else {
        processedFilters[key] = value;
      }
    });

    return processedFilters;
  };

  // Helper function to convert date format from "mm/dd/yyyy" to "yyyy-mm-dd"
  const convertDateFormat = (dateString: string): string => {
    if (!dateString) return dateString;

    // Split the date string by "/" to get parts
    const parts = dateString.split("/");
    if (parts.length !== 3) return dateString;

    const [month, day, year] = parts;

    // Ensure month and day are zero-padded
    const paddedMonth = month.padStart(2, "0");
    const paddedDay = day.padStart(2, "0");

    // Return in yyyy-mm-dd format
    return `${year}-${paddedMonth}-${paddedDay}`;
  };

  const fetchAllOrders = () => {
    if (validFilters?.[0]?.["filterCount"] > 0 && searchTerm.length <= 3) {
      const finalFilterDataToSend = processFilterData(validFilters);
      setFilterState(true);
      dispatch(
        fetchFinalWorklistOrdersFromApi(finalLimitToUse, finalFilterDataToSend),
      );
    } else {
      setFilterState(false);
      dispatch(fetchFinalWorklistOrdersFromApi(finalLimitToUse));
    }
  };

  const search = () => {
    dispatch(searchFinalWorklistOrdersFromApi(searchTerm, finalLimitToUse));
  };

  const handleBulkAssignButtonClick = () => {
    onBulkAssignButtonClick();
  };

  const exportDataForTable = () => {
    let filteredData = finalWorklistData;

    const data = exportDataFromTableForExternalPaOrders(
      filteredData ?? [],
      authStatusOptions ?? [],
    );
    exportDataToExcel(
      data,
      `RISA Auth Orders Export-${moment().format("YYYY-MM-DD")}`,
    );
  };

  const handleRefreshButtonClick = () => {
    setTableDataForContext("finalWorklistOrders", { lastBatchFetched: 0 });
    setBatchNumberRetreived([]);
    const isValidSearch = searchTerm && searchTerm.length >= 3;
    isValidSearch ? search() : fetchAllOrders();
  };

  const validFilters = useMemo(() => {
    return filters.filter((filter) => filter.name === "finalWorklistOrders");
  }, [filters]);

  const handleEndIndexOfTable = (endIndex: number) => {
    setEndIndex(endIndex);
    logDataToConsole("End index of table", endIndex);

    if (
      finalWorklistData &&
      pageData?.total_records !== null &&
      endIndex < (pageData?.total_records ?? 0) - 1 &&
      endIndex === finalWorklistData.length - 1
    ) {
      const nextBatchFetch = Math.round(
        finalWorklistData.length / finalLimitToUse,
      );
      if (nextBatchFetch > 0) {
        setBatchNumberRetreived((prevBatchNumberRetrieved) => {
          if (!prevBatchNumberRetrieved.includes(nextBatchFetch)) {
            setTableDataForContext("finalWorklistOrders", {
              lastBatchFetched: nextBatchFetch,
            });
            return [...prevBatchNumberRetrieved, nextBatchFetch];
          }
          return prevBatchNumberRetrieved;
        });
      }
    }
  };

  const handleSearch = (searchText: string) => {
    setSearchTerm(searchText);
    setInitialSearch(false);
  };

  const resetAndFetchOrders = () => {
    dispatch(resetFinalWorklistOrders());
    setTableDataForContext("finalWorklistOrders", { lastBatchFetched: 0 });
    setBatchNumberRetreived([]);
    const isValidSearch = searchTerm && searchTerm.length >= 3;
    isValidSearch ? search() : fetchAllOrders();
  };

  // Handle search changes
  useEffect(() => {
    if (isTableRefActive) {
      setBatchNumberRetreived([]);

      const isValidSearch = searchTerm && searchTerm.length >= 3;
      if (isValidSearch) {
        search();
      } else {
        fetchAllOrders();
      }
    }
  }, [searchTerm, isTableRefActive]);

  useEffect(() => {
    dispatch(fetchAnalyticsDataFromApi());

    if (error) {
      logDataToConsole(error);
      toast.error(ToastMessages.ERROR_ANALYTICS);
    }
  }, []);

  // Handle batch pagination
  useEffect(() => {
    if (batchNumberRetreived.length > 0 && filterState === false) {
      if (searchTerm && searchTerm.length > 3) {
        dispatch(
          appendFinalWorklistSearchedOrdersFromApi(searchTerm, finalLimitToUse),
        );
      } else {
        dispatch(appendFinalWorklistOrdersFromApi(finalLimitToUse));
      }
    }
  }, [JSON.stringify(batchNumberRetreived)]);

  // Handle filter changes
  useEffect(() => {
    if (
      validFilters?.[0]?.["filterCount"] > 0 &&
      filterForTable?.filterCount &&
      filterForTable?.filterCount > 0 &&
      searchTerm.length <= 3
    ) {
      setFilterState(true);
      const finalFilterDataToSend = processFilterData(validFilters);
      setTableDataForContext("finalWorklistOrders", {
        endIndex: 0,
        startIndex: 0,
        currentActiveArrayIndex: 0,
        currentStartPage: 1,
        lastBatchFetched: 0,
      });
      dispatch(
        fetchFinalWorklistOrdersFromApi(finalLimitToUse, finalFilterDataToSend),
      );
    } else {
      !initialSearch && fetchAllOrders();
      setFilterState(false);
    }
  }, [JSON.stringify(filterForTable)]);

  // Handle filter state pagination
  useEffect(() => {
    if (filterState) {
      if (endIndex === tableData.length - 1) {
        const tableAttributes = getTableDataFromContext("finalWorklistOrders");
        const lastBatchFetched = tableAttributes.lastBatchFetched;
        const latestBatchToFetch = (lastBatchFetched ?? 0) + 1;
        setTableDataForContext("finalWorklistOrders", {
          lastBatchFetched: latestBatchToFetch,
        });
        const finalFilterDataToSend = processFilterData(validFilters);
        dispatch(
          appendFinalWorklistOrdersFromApi(
            finalLimitToUse,
            finalFilterDataToSend,
          ),
        );
      }
    }
  }, [filterState, endIndex]);

  const isFilterApplied = useMemo(() => {
    return (
      validFilters &&
      validFilters.length > 0 &&
      validFilters?.[0]?.["filterCount"] > 0
    );
  }, [validFilters]);

  // Process table data when finalWorklistData changes
  useEffect(() => {
    if (finalWorklistData && finalWorklistData.length > 0) {
      setHasInitialDataLoaded(true);
      let filteredData = finalWorklistData;

      const tableData =
        isCurrentUserAdmin() || isCurrentUserSuperAdmin()
          ? generatFinalWorklistTableData(
              filteredData as FinalWorklistDataResponse[],
              authStatusOptionsForFinalWorklist ?? [],
            )
          : generateMedicalPaOrdersTableDataForNonAdmin(
              filteredData,
              authStatusOptionsForFinalWorklist ?? [],
            );
      setTableData(tableData);
    } else {
      setTableData([]);
    }
  }, [
    finalWorklistData,
    validFilters,
    isCurrentUserAdmin(),
    isCurrentUserSuperAdmin(),
  ]);

  // Set table ref active
  useEffect(() => {
    if (medicalPaOrdersTableRef.current) {
      setIsTableRefActive(true);
    }
  }, [medicalPaOrdersTableRef]);

  // Set filter data
  useEffect(() => {
    if (finalWorklistData && finalWorklistData.length > 0) {
      const internalOrganizationFacilityId = UserStore.getItem(
        UserGeneralInfoContextKeys.INTERNAL_ORGANIZATION_FACILITY_ID,
      );
      const filterData = [
        createFilterForCreatedAt(),
        createFilterForDateOfService(),
        createFilterForDrugNames(analyticsData),
        createFilterForOrderStatus(analyticsData),
        createFilterForAuthStatus(analyticsData),
        createFilterForPrimaryInsurance(analyticsData),
      ];
      if (internalOrganizationFacilityId !== OrganizationType.NYCBS) {
        filterData.push(createFilterForLocation(analyticsData));
      }
      setFilterData(filterData);
    }
  }, [finalWorklistData, providerDetails, analyticsData]);

  const handleFilterApply = (data: FilterValues) => {
    const updatedData = {
      ...data,
      name: "finalWorklistOrders",
    };
    setFilterForTable(updatedData);
    dispatch(setFilter(updatedData));
  };

  // Handle completion refresh
  useEffect(() => {
    if (onComplete) {
      handleRefreshButtonClick();
    }
  }, [onComplete]);

  return (
    <>
      <div
        ref={medicalPaOrdersTableRef}
        className="final-worklist-table--container h-full"
      >
        <CustomTable
          showBulkAssignButton={false}
          bulkAssignButtonDisabled={true}
          onBulkAssignButtonClick={handleBulkAssignButtonClick}
          tableHeaders={
            UserStore.getItem(
              UserGeneralInfoContextKeys.INTERNAL_ORGANIZATION_FACILITY_ID,
            ) === OrganizationType.NYCBS
              ? FinalWorklistTableHeadersForNYCBS
              : FinalWorklistTableHeaders
          }
          tableData={tableData}
          tableName={"finalWorklistOrders"}
          itemsPerPage={10}
          pagesPerView={5}
          count={filterState ? tableData?.length : finalWorklistData?.length}
          totalCount={pageData?.total_records}
          endIndexOfTable={handleEndIndexOfTable}
          searchingText={handleSearch}
          hideSearchBar={false}
          isFetching={isLoadingFinalWorklist || isLoadingAnalyticsData}
          showExportButton={true}
          onExportButtonClick={exportDataForTable}
          showInLineLoader={showInLineLoader}
          showRefreshButton={true}
          onRefreshButton={resetAndFetchOrders}
          onComplete={handleRefreshButtonClick}
          showFilterButton={
            hasInitialDataLoaded && searchTerm.length <= 3 ? true : false
          }
          filterSectionsData={filterData}
          onFilterApplyClicked={handleFilterApply}
          isDisabled={isLoadingFinalWorklist}
          isFilterApplied={isFilterApplied}
          handleDownloadButtonClick={(id: string) => {
            const rowData = finalWorklistData?.find((item) => item.id === id);
            onDownloadButtonClick(id, rowData);
          }}
        />
      </div>
    </>
  );
};

export default FinalWorklistOrdersTable;
